import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getLatestPayments } from '../../services/paymentService';

const NOTIFICATION_DURATION_MS = 10000;
const POLL_INTERVAL_MS = 10000;
/** Only toast purchases within this window (avoids backfill / stale poll noise). */
const MAX_PURCHASE_AGE_MS = 30 * 60 * 1000;

const pad2 = (n) => String(n).padStart(2, '0');

/** API expects Y-m-d H:i:s in server timezone. */
const formatTimestampForApi = (date = new Date()) => {
  const tz = process.env.REACT_APP_SERVER_TIMEZONE || 'Z';
  if (tz === 'Z' || tz === 'UTC') {
    return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())} `
      + `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} `
    + `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

const parsePurchasedAt = (purchasedAt) => {
  if (!purchasedAt) return null;
  if (typeof purchasedAt === 'string' && purchasedAt.includes(' ')) {
    const tz = process.env.REACT_APP_SERVER_TIMEZONE || 'Z';
    if (purchasedAt.match(/[+-]\d{2}:\d{2}$|Z$/)) {
      const parsed = new Date(purchasedAt);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const [datePart, timePart] = purchasedAt.split(' ');
    const [year, month, day] = datePart.split('-');
    const [hour, minute, second] = timePart.split(':');
    const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T`
      + `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}${tz}`;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(purchasedAt);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isRecentPurchase = (purchasedAt) => {
  const purchased = parsePurchasedAt(purchasedAt);
  if (!purchased) return false;
  return Date.now() - purchased.getTime() <= MAX_PURCHASE_AGE_MS;
};

const LatestPaymentsNotifications = () => {
  const navigate = useNavigate();
  const [activePayments, setActivePayments] = useState([]);
  const lastTimestampRef = useRef(null);
  const seenEventIdsRef = useRef(new Set());
  const hideTimeoutRef = useRef(null);

  const markPaymentsSeen = (payments) => {
    payments.forEach((p) => {
      if (p.event_id) seenEventIdsRef.current.add(p.event_id);
    });
  };

  const fetchLatestPayments = async (since = null, { notify = true } = {}) => {
    try {
      const res = await getLatestPayments(since, 10);
      if (res.data.success) {
        const payments = res.data.data?.payments ?? [];
        if (res.data.data?.latest_timestamp) {
          lastTimestampRef.current = res.data.data.latest_timestamp;
        }
        if (!payments.length) return;
        if (notify && since) {
          processNewPayments(payments);
        } else {
          markPaymentsSeen(payments);
        }
      }
    } catch (error) {
      console.error('Error fetching latest payments:', error);
    }
  };

  const processNewPayments = (payments) => {
    const newItems = payments.filter((p) => {
      if (!p.event_id) return false;
      if (seenEventIdsRef.current.has(p.event_id)) return false;
      seenEventIdsRef.current.add(p.event_id);
      return isRecentPurchase(p.purchased_at);
    });
    if (newItems.length === 0) return;

    setActivePayments((prev) => {
      const next = [...prev];
      newItems.forEach((payment) => next.push({ id: payment.event_id, payment, addedAt: Date.now() }));
      return next;
    });

    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setActivePayments([]);
      hideTimeoutRef.current = null;
    }, NOTIFICATION_DURATION_MS);
  };

  const handleRemove = (id) => {
    setActivePayments((prev) => prev.filter((item) => item.id !== id));
  };

  const formatLabel = (payment) => {
    const usd = payment.price_usd != null ? `$${Number(payment.price_usd).toFixed(2)}` : '';
    const product = payment.product_id?.split('.').pop() || 'purchase';
    return `${usd} · ${product}`;
  };

  useEffect(() => {
    let intervalId = null;
    const init = async () => {
      const now = formatTimestampForApi();
      lastTimestampRef.current = now;
      // Seed cursor at "now" so we do not replay historical purchases after backfill.
      await fetchLatestPayments(now, { notify: false });
      intervalId = setInterval(() => {
        if (lastTimestampRef.current) {
          fetchLatestPayments(lastTimestampRef.current);
        }
      }, POLL_INTERVAL_MS);
    };
    init();
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  if (activePayments.length === 0) return null;

  return (
    <>
      {activePayments.map((item, index) => {
        const topPosition = 20 + index * 90;
        const { payment } = item;
        return (
          <div
            key={item.id}
            className="latest-songs-popup-item rounded p-2 shadow"
            style={{
              position: 'fixed',
              top: topPosition,
              right: 20,
              zIndex: 9999 - index,
              minWidth: 280,
              background: '#1a3a2a',
              color: '#fff',
              border: '1px solid #2d6a4f',
            }}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/payments?device_id=${encodeURIComponent(payment.device_id)}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/payments?device_id=${encodeURIComponent(payment.device_id)}`)}
          >
            <div className="d-flex align-items-start justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faCreditCard} className="text-success" />
                <div>
                  <div className="small fw-bold">New payment</div>
                  <div className="small opacity-75">{formatLabel(payment)}</div>
                  <div className="small font-monospace opacity-75">{payment.device_id?.slice(0, 12)}…</div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-link btn-sm text-white p-0"
                onClick={(e) => { e.stopPropagation(); handleRemove(item.id); }}
                aria-label="Dismiss"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default LatestPaymentsNotifications;
